import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Row,
  Col,
  Button,
  Table,
  Breadcrumb,
  Tooltip,
  InputNumber,
  Select,
  Space,
  Radio,
  Checkbox,
  Form,
  Input,
  Modal,
} from "antd";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import "../../../styles/Main.scss";
import { useTranslation } from "react-i18next";
import { getRequest, patchRequest, postRequest } from "../../../axios";
import { authUserData } from "../../../utils/Helper";
import { ShowErrorMsg, SuccessNotificationMsg } from "../../../utils/Notifications";
import { EditOutlined, InfoCircleOutlined, MinusCircleOutlined, PlusCircleOutlined, PlusOutlined } from "@ant-design/icons";

const AcceptanceDist = ({
  embedded,
  modeProp,
  idProp,
  onBack,
  documentType,
} = {}) => {
  const { t } = useTranslation();
  const CompanyId = authUserData()?.company || null;
  const { mode: modeParam, id: idParam } = useParams();
  const mode = modeProp ?? modeParam;
  const id = idProp ?? idParam;
  const navigate = useNavigate();
  const location = useLocation();
  const queryDocTypeForUi = new URLSearchParams(location?.search || "").get(
    "document_type",
  );
  const uiDocumentType = documentType ?? queryDocTypeForUi;
  const isChallanUi =
    String(uiDocumentType || "").toLowerCase().trim() === "challan";
  const [loading, setLoading] = useState(false);
  const [qtyErrors, setQtyErrors] = useState({});
  const [receiptHeader, setReceiptHeader] = useState({
    grnNumber: "—",
    invoiceRef: "—",
    supplierName: "—",
    receivedDate: "—",
    siteName: "—",
  });
  const [receiptIds, setReceiptIds] = useState({
    recordId: null,
    documentId: null,
    vendorId: null,
    siteId: null,
  });
  const [linkedDocIds, setLinkedDocIds] = useState({
    linkedInvoiceId: null,
    linkedChallanIds: [],
  });
  const [submitting, setSubmitting] = useState(false);

  const isViewMode = (mode || "").toLowerCase() === "view";

  useEffect(() => {
    if ((mode || "").toLowerCase() === "inspection") {
      setSelectedRadio("radio1");
    } else if ((mode || "").toLowerCase() === "distribution") {
      setSelectedRadio("radio2");
    } else if ((mode || "").toLowerCase() === "view") {
      setSelectedRadio("radio1");
    }
  }, [mode]);

  useEffect(() => {
    if (!id) return;
    const fetchGoodsReceipt = async () => {
      try {
        setLoading(true);
        const q = new URLSearchParams(location?.search || "");
        const queryDocType = q.get("document_type");
        const effectiveDocType = documentType ?? queryDocType;
        const params =
          effectiveDocType != null && effectiveDocType !== ""
            ? { document_type: effectiveDocType }
            : undefined;
        const res = await getRequest(`inventory-goods-receipts/${id}/`, params);
        const payload = res?.data;
        const rawData = payload?.data ?? payload?.results?.data ?? payload;
        if (!rawData) return;
        
        const record = Array.isArray(rawData) ? rawData[0] : rawData;
        if (!record) return;
        const isChallanDoc = String(effectiveDocType || "").toLowerCase() === "challan";
        setReceiptHeader({
          grnNumber: record?.grn_number ?? "—",
          invoiceRef: isChallanDoc
            ? (record?.challan_number ?? record?.invoice_number ?? "")
            : (record?.invoice_number ?? ""),
          supplierName: record?.vendor_details?.name ?? "",
          receivedDate: record?.received_date ?? "—",
          siteName: record?.site_name ?? "—",
        });
        setReceiptIds({
          recordId: record?.id ?? null,
          documentId: record?.document ?? null,
          vendorId: record?.vendor ?? null,
          siteId: record?.site ?? null,
        });
        // Linked docs (invoice <-> challan) so we can optionally update both flows.
        // API payloads/URLs use the goods receipt id (same as route `id` / `documentId`).
        const linkedInvoiceId =
          record?.linked_invoice?.invoice_id ??
          record?.linked_invoice?.id ??
          record?.linked_invoice?.document ??
          null;
        const linkedChallanIds = Array.isArray(record?.linked_challans)
          ? record.linked_challans
              .map(
                (c) =>
                  c?.id ?? c?.challan_id ?? c?.document ?? c?.challan_uuid ?? null,
              )
              .filter((v) => v != null)
          : [];
        setLinkedDocIds({
          linkedInvoiceId,
          linkedChallanIds,
        });
        const rawUserDetails = Array.isArray(record?.user_details)
          ? record.user_details
          : [];
        const normalizeStage = (s) => String(s || "").toUpperCase().trim();
        const inspectionUsers = rawUserDetails
          .filter((u) => normalizeStage(u?.stage) === "INSPECTION")
          .map((u, i) => ({
            id: `user-inspection-${record?.id ?? id}-${i}`,
            name: u?.name ?? "",
            email: u?.email ?? "",
            mobile: u?.mobile ?? "",
          }));
        const distributionUsers = rawUserDetails
          .filter((u) => normalizeStage(u?.stage) === "DISTRIBUTION")
          .map((u, i) => ({
            id: `user-distribution-${record?.id ?? id}-${i}`,
            name: u?.name ?? "",
            email: u?.email ?? "",
            mobile: u?.mobile ?? "",
          }));
        setInspectionUserDetails(inspectionUsers);
        setDistributionUserDetails(distributionUsers);
        const lineItems = Array.isArray(record?.items)
          ? record.items
          : record?.item_name != null || record?.quantity != null
            ? [record]
            : [];
        const anyInspections = lineItems.some(
          (it) => Array.isArray(it?.inspections) && it.inspections.length > 0
        );
        const anyDistribution = lineItems.some(
          (it) =>
            Array.isArray(it?.distribution_sites) &&
            it.distribution_sites.length > 0
        );
        setHasInspectionHistory(anyInspections);
        setHasDistributionHistory(anyDistribution);
        if (lineItems.length > 0) {
          const ACCEPTANCE_FROM_API = {
            ACCEPT_ALL: "Fully Accepted",
            REJECT_ALL: "Rejected",
            PARTIAL: "Partially Accepted",
          };
          const REJECTION_REASON_FROM_API = {
            DAMAGED_IN_TRANSIT: "Damaged in Transit",
            QUALITY_ISSUE: "Quality Issue",
            EXPIRED_MATERIAL: "Expired Material",
            INCORRECT_SPECIFICATION: "Incorrect Specification",
            QUANTITY_MISMATCH: "Quantity Mismatch",
          };
          const useInspectionData =
            (mode || "").toLowerCase() === "inspection" ||
            (mode || "").toLowerCase() === "view";
          const mapped = lineItems.map((it, idx) => {
            const inspections = Array.isArray(it?.inspections)
              ? it.inspections
              : [];
            const latestInspection =
              useInspectionData && inspections.length > 0
                ? inspections[inspections.length - 1]
                : null;
            const acceptedQtyVal = latestInspection
              ? Number(latestInspection.accepted_qty)
              : Number(0);
            const rejectedQtyVal = latestInspection
              ? Number(latestInspection.rejected_qty)
              : 0;
            const holdQtyVal = latestInspection
              ? Number(latestInspection.hold_qty)
              : 0;
            const rejectionReasonVal = latestInspection?.rejection_reason
              ? REJECTION_REASON_FROM_API[latestInspection.rejection_reason] ??
                String(latestInspection.rejection_reason)
              : "";
            const holdReasonVal = latestInspection?.hold_reason
              ? REJECTION_REASON_FROM_API[latestInspection.hold_reason] ??
                String(latestInspection.hold_reason)
              : "";
            const acceptanceVal = latestInspection?.acceptance
              ? ACCEPTANCE_FROM_API[latestInspection.acceptance] ??
                "Partially Accepted"
              : "";
            return {
              key: it?.id ?? idx + 1,
              id: it?.id,
              item: it?.item_name ?? it?.raw_item_name ?? it?.ai_mapped_name ?? "—",
              invoiceQty: Number(it?.quantity ?? 0) || 0,
              acceptedQty: Number.isFinite(acceptedQtyVal) ? acceptedQtyVal : 0,
              holdQty: Number.isFinite(holdQtyVal) ? holdQtyVal : 0,
              rejectedQty: Number.isFinite(rejectedQtyVal) ? rejectedQtyVal : 0,
              rejectionReason: rejectionReasonVal ?? "",
              holdReason: holdReasonVal ?? "",
              acceptance: acceptanceVal,
            };
          });
          setItems(mapped);

          const isDistributionOrView =
            (mode || "").toLowerCase() === "distribution" ||
            (mode || "").toLowerCase() === "view";
          if (isDistributionOrView) {
            setQcUI((prev) => {
              const next = { ...prev };
              lineItems.forEach((it, idx) => {
                const key = it?.id ?? idx + 1;
                const distSites = Array.isArray(it?.distribution_sites)
                  ? it.distribution_sites
                  : [];
                next[key] =
                  distSites.length > 0
                    ? distSites.map((ds, i) => ({
                        id: ds?.id ?? `${key}-dist-${i}`,
                        site: ds?.site ?? null,
                        qty: Number(ds?.quantity) || 0,
                      }))
                    : [{ id: `${key}-0`, site: null, qty: 0 }];
              });
              return next;
            });
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchGoodsReceipt();
  }, [id, mode, documentType, location?.search]);

  const [selectedRadio, setSelectedRadio] = useState("radio1");
  const [items, setItems] = useState([]);
  const [siteLocationList, setSiteLocationList] = useState([]);
  const [qcUI, setQcUI] = useState({});
  const [inspectionUserDetails, setInspectionUserDetails] = useState([]);
  const [distributionUserDetails, setDistributionUserDetails] = useState([]);
  const [hasInspectionHistory, setHasInspectionHistory] = useState(false);
  const [hasDistributionHistory, setHasDistributionHistory] = useState(false);

  const addInspectionUserDetailRow = () => {
    setInspectionUserDetails((prev) => [
      ...prev,
      { id: `inspection-${Date.now()}-${Math.random()}`, name: "", email: "", mobile: "" },
    ]);
  };
  const removeInspectionUserDetailRow = (id) => {
    setInspectionUserDetails((prev) => prev.filter((r) => r.id !== id));
  };
  const updateInspectionUserDetailRow = (id, field, value) => {
    setInspectionUserDetails((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    );
  };

  const addDistributionUserDetailRow = () => {
    setDistributionUserDetails((prev) => [
      ...prev,
      { id: `distribution-${Date.now()}-${Math.random()}`, name: "", email: "", mobile: "" },
    ]);
  };
  const removeDistributionUserDetailRow = (id) => {
    setDistributionUserDetails((prev) => prev.filter((r) => r.id !== id));
  };
  const updateDistributionUserDetailRow = (id, field, value) => {
    setDistributionUserDetails((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    );
  };

  const getCompanySites = async () => {
    try {
      const res = await getRequest(
        `delivery-address/${CompanyId}/get-address-by-company/`
      );
      setSiteLocationList(res?.data?.results?.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (CompanyId) getCompanySites();
  }, [CompanyId]);

  const isDistributionMode = (mode || "").toLowerCase() === "distribution";

  useEffect(() => {
    if (!items?.length) return;
    const usePanelSites =
      isDistributionMode && siteLocationList.length > 0;
    setQcUI((prev) => {
      const next = { ...prev };
      items.forEach((it) => {
        const existing = next[it.key];
        const hasExistingDistribution =
          Array.isArray(existing) && existing.length > 0;
        if (usePanelSites) {
          if (!hasExistingDistribution) {
            next[it.key] = siteLocationList.map((site, idx) => ({
              id: `${it.key}-${idx}`,
              site: site?.id ?? site?.site_name,
              qty: 0,
            }));
          }
        } else if (!hasExistingDistribution) {
          next[it.key] = [{ id: `${it.key}-0`, site: null, qty: 0 }];
        }
      });
      return next;
    });
  }, [items?.length, isDistributionMode, siteLocationList.length]);

  const addDistributeRow = (rowKey) => {
    setQcUI((prev) => ({
      ...prev,
      [rowKey]: [...(prev[rowKey] || []), { id: `${rowKey}-${Date.now()}`, site: null, qty: 0 }],
    }));
  };

  const updateDistributeRow = (rowKey, index, field, value, totalReceivedQty) => {
    setQcUI((prev) => {
      const list = prev[rowKey] || [];
      if (field === "qty" && totalReceivedQty != null) {
        const otherSum = list.reduce(
          (s, r, i) => (i !== index ? s + (Number(r.qty) || 0) : s),
          0
        );
        const maxQty = Math.max(0, (Number(totalReceivedQty) || 0) - otherSum);
        const clamped = Math.min(Math.max(Number(value) || 0, 0), maxQty);
        return {
          ...prev,
          [rowKey]: list.map((r, i) =>
            i === index ? { ...r, [field]: clamped } : r
          ),
        };
      }
      return {
        ...prev,
        [rowKey]: list.map((r, i) =>
          i === index ? { ...r, [field]: value } : r
        ),
      };
    });
  };

  const removeDistributeRow = (rowKey, index) => {
    setQcUI((prev) => ({
      ...prev,
      [rowKey]: (prev[rowKey] || []).filter((_, i) => i !== index),
    }));
  };
  const REJECTION_REASON_OPTIONS = [
    { label: "Damaged in Transit", value: "Damaged in Transit" },
    { label: "Quality Issue", value: "Quality Issue" },
    { label: "Expired Material", value: "Expired Material" },
    { label: "Incorrect Specification", value: "Incorrect Specification" },
    { label: "Quantity Mismatch", value: "Quantity Mismatch" },
    { label: "Lost in Transit", value: "LOST_IN_TRANSIT" },
  ];
  const [tableData, setTableData] = useState([]);

  const columns = [
    {
      title: "Item",
      dataIndex: "item",
      width: 200,
      align: "left",
    },
    {
      title: "Total Received Qty",
      dataIndex: "invoiceQty",
      align: "center",
      width: 140,
    },
    {
      title: "Accepted Qty",
      dataIndex: "acceptedQty",
      width: 120,
      align: "center",
      render: (value, record) => {
        const errorMsg = qtyErrors[record.key];
        const isInspection = (mode || "").toLowerCase() === "inspection";

        const input = (
          <InputNumber
            min={0}
            value={value}
            style={{
              width: "100%",
              borderColor: errorMsg && isInspection ? "#ff4d4f" : undefined,
            }}
            onChange={(val) =>
              updateItemRow(record.key, "acceptedQty", val ?? 0)
            }
          />
        );

        if (isViewMode) return <span>{value ?? "—"}</span>;
        return (
          <Tooltip title={errorMsg && isInspection ? errorMsg : undefined}>
            {input}
          </Tooltip>
        );
      },
    },
    {
      title: "Hold Qty",
      dataIndex: "holdQty",
      width: 120,
      align: "center",
      render: (value, record) => {
        const errorMsg = qtyErrors[record.key];
        const isInspection = (mode || "").toLowerCase() === "inspection";

        const input = (
          <InputNumber
            min={0}
            value={value}
            style={{
              width: "100%",
              borderColor: errorMsg && isInspection ? "#ff4d4f" : undefined,
            }}
            onChange={(val) =>
              updateItemRow(record.key, "holdQty", val ?? 0)
            }
          />
        );

        if (isViewMode) return <span>{value ?? "—"}</span>;
        return (
          <Tooltip title={errorMsg && isInspection ? errorMsg : undefined}>
            {input}
          </Tooltip>
        );
      },
    },
    {
      title: "Rejected Qty",
      dataIndex: "rejectedQty",
      width: 120,
      align: "center",
      render: (value, record) => {
        const errorMsg = qtyErrors[record.key];
        const isInspection = (mode || "").toLowerCase() === "inspection";

        const input = (
          <InputNumber
            min={0}
            value={value}
            style={{
              width: "100%",
              borderColor: errorMsg && isInspection ? "#ff4d4f" : undefined,
            }}
            onChange={(val) =>
              updateItemRow(record.key, "rejectedQty", val ?? 0)
            }
          />
        );

        if (isViewMode) return <span>{value ?? "—"}</span>;
        return (
          <Tooltip title={errorMsg && isInspection ? errorMsg : undefined}>
            {input}
          </Tooltip>
        );
      },
    },
    {
      title: "Total Qty",
      dataIndex: "totalQty",
      width: 120,
      align: "center",
      render: (_, record) => {
        const totalReceived = Number(record?.invoiceQty) || 0;
        const accepted = Number(record?.acceptedQty) || 0;
        const hold = Number(record?.holdQty) || 0;
        const rejected = Number(record?.rejectedQty) || 0;
        const sum = accepted + hold + rejected;
        return `${sum} / ${totalReceived}`;
      },
    },
    {
      title: "Hold Reason",
      dataIndex: "holdReason",
      key: "holdReason",
      width: 200,
      align: "left",
      render: (value, record) => {
        const hasHold = (Number(record?.holdQty) || 0) > 0;
        if (isViewMode) return <span>{value || "—"}</span>;
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, width: "100%" }}>
            {hasHold && <span style={{ color: "#ff4d4f", flexShrink: 0 }}>*</span>}
            <Select
              value={value || undefined}
              allowClear
              disabled={!hasHold}
              style={{ flex: 1, minWidth: 0 }}
              placeholder="Select Hold Reason"
              onChange={(val) => updateItemRow(record.key, "holdReason", val)}
            >
              {REJECTION_REASON_OPTIONS.map((r) => (
                <Select.Option key={r.value} value={r.value}>
                  {r.label}
                </Select.Option>
            ))}
          </Select>
        </span>
        );
      },
    },
    {
      title: "Rejection Reason",
      dataIndex: "rejectionReason",
      key: "rejectionReason",
      width: 200,
      align: "left",
      render: (value, record) => {
        const hasRejected = (Number(record?.rejectedQty) || 0) > 0;
        if (isViewMode) return <span>{value || "—"}</span>;
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, width: "100%" }}>
            {hasRejected && <span style={{ color: "#ff4d4f", flexShrink: 0 }}>*</span>}
            <Select
              value={value || undefined}
              allowClear
              disabled={!hasRejected}
              style={{ flex: 1, minWidth: 0 }}
              placeholder="Select Rejection Reason"
              onChange={(val) => updateItemRow(record.key, "rejectionReason", val)}
            >
              {REJECTION_REASON_OPTIONS.map((r) => (
                <Select.Option key={r.value} value={r.value}>
                  {r.label}
                </Select.Option>
            ))}
          </Select>
        </span>
        );
      },
    },

  ];
  const columns2 = [
    {
      title: "Item",
      dataIndex: "item",
      width: 200,
      align: "left",
    },
    {
      title: "Total Received Qty",
      dataIndex: "invoiceQty",
      width: 140,
      align: "center",
    },
    {
      title: "Distribute Qty",
      width: 320,
      dataIndex: "DistributeQty",
      render: (value, record) => {
        const distRows = qcUI[record.key] ?? [{ id: `${record.key}-0`, site: null, qty: 0 }];
        const totalReceived = Number(record?.invoiceQty) || 0;
        const distributedSum = distRows.reduce(
          (s, r) => s + (Number(r.qty) || 0),
          0
        );
        const canAddMoreSites =
          distRows.length < siteLocationList.length &&
          distributedSum < totalReceived;
        if (isViewMode) {
          const lines = distRows
            .filter((r) => r.site != null && (Number(r.qty) || 0) > 0)
            .map((r) => {
              const siteObj = siteLocationList.find(
                (s) => (s?.id ?? s?.site_name) === r.site
              );
              const siteName = siteObj?.site_name ?? siteObj?.company_name ?? r.site ?? "—";
              return `${siteName}: ${Number(r.qty) || 0}`;
            });
          return <span>{lines.length ? lines.join(", ") : "—"}</span>;
        }
        return (
          <Form form={form} name="control-hooks" style={{ maxWidth: 600 }}>
            <Space
              direction="vertical"
              align="start"
              style={{ alignItems: "flex-start" }}
            >
              {distRows.map((row, index) => {
                const otherSum = distRows.reduce(
                  (s, r, i) => (i !== index ? s + (Number(r.qty) || 0) : s),
                  0
                );
                const maxQty = Math.max(0, totalReceived - otherSum);
                const selectedByOtherRows = distRows
                  .filter((_, i) => i !== index)
                  .map((r) => r.site)
                  .filter(Boolean);
                const siteOptions = siteLocationList
                  .filter(
                    (s) =>
                      !selectedByOtherRows.includes(s?.id ?? s?.site_name)
                  )
                  .map((s) => ({
                    label: s?.site_name ?? s?.company_name ?? s?.id,
                    value: s?.id ?? s?.site_name,
                  }));
                const rowIsValid =
                  row.site != null && (Number(row.qty) || 0) > 0;
                const showPlus =
                  index === distRows.length - 1 &&
                  canAddMoreSites &&
                  rowIsValid;
                const showMinus = distRows.length > 1 && rowIsValid;
                return (
                  <Space key={row.id} direction="horizontal" align="start">
                    <Select
                      allowClear
                      placeholder="Select Site"
                      value={row.site}
                      style={{ width: 200 }}
                      onChange={(val) =>
                        updateDistributeRow(record.key, index, "site", val)
                      }
                      options={siteOptions}
                    />

                    <InputNumber
                      placeholder="QTY"
                      min={0}
                      max={maxQty}
                      value={row.qty}
                      style={{ width: 100 }}
                      disabled={row.site == null}
                      onChange={(val) =>
                        updateDistributeRow(
                          record.key,
                          index,
                          "qty",
                          val,
                          record.invoiceQty
                        )
                      }
                    />

                    {showPlus && (
                      <PlusCircleOutlined
                        style={{
                          fontSize: 18,
                          color: "#52c41a",
                          cursor: "pointer",
                        }}
                        onClick={() => addDistributeRow(record.key)}
                      />
                    )}

                    {showMinus && (
                      <MinusCircleOutlined
                        style={{ fontSize: 18, color: "red", cursor: "pointer" }}
                        onClick={() => removeDistributeRow(record.key, index)}
                      />
                    )}
                  </Space>
                );
              })}
            </Space>
          </Form>
        );
      },
    },
    {
      title: "Total Distributed",
      width: 140,
      align: "center",
      render: (_, record) => {
        const distRows = qcUI[record.key] ?? [];
        const sum = distRows.reduce(
          (s, r) => s + (Number(r.qty) || 0),
          0
        );
        return `${sum}/${record?.invoiceQty ?? 0}`;
      },
    },
  ];

  const distributionPanelColumns = [
    {
      title: "Item",
      dataIndex: "item",
      width: 200,
      align: "left",
      onHeaderCell: () => ({ style: { background: "#094e8f", color: "#fff", fontWeight: 600 } }),
    },
    {
      title: "Total Received Qty.",
      dataIndex: "invoiceQty",
      width: 140,
      align: "center",
      onHeaderCell: () => ({ style: { background: "#094e8f", color: "#fff", fontWeight: 600 } }),
    },
    ...(siteLocationList.map((site, idx) => {
      const siteId = site?.id ?? site?.site_name;
      return {
        title: site?.site_name ?? site?.company_name ?? "Site",
        key: `site-${site?.id}`,
        width: 120,
        align: "center",
        onHeaderCell: () => ({ style: { background: "#094e8f", color: "#fff", fontWeight: 600 } }),
        render: (_, record) => {
          const distRows = qcUI[record.key] ?? [];
          const rowIndex = distRows.findIndex(
            (r) => r.site == siteId || String(r.site) === String(siteId)
          );
          const row = rowIndex >= 0 ? distRows[rowIndex] : { qty: 0 };
          const totalReceived = Number(record?.invoiceQty) || 0;
          const otherSum = distRows.reduce(
            (s, r, i) => (i !== rowIndex ? s + (Number(r.qty) || 0) : s),
            0
          );
          const maxQty = Math.max(0, totalReceived - otherSum);
          if (isViewMode) {
            return <span>{Number(row.qty) || 0}</span>;
          }
          const handleQtyChange = (val) => {
            if (rowIndex >= 0) {
              updateDistributeRow(record.key, rowIndex, "qty", val, record.invoiceQty);
            } else if (Number(val) > 0) {
              setQcUI((prev) => ({
                ...prev,
                [record.key]: [
                  ...(prev[record.key] || []),
                  {
                    id: `${record.key}-${siteId}-${Date.now()}`,
                    site: siteId,
                    qty: Number(val) || 0,
                  },
                ],
              }));
            }
          };
          return (
            <InputNumber
              placeholder="Enter Qty."
              min={0}
              max={maxQty}
              value={row.qty}
              style={{ width: "100%", maxWidth: 140 }}
              onChange={handleQtyChange}
            />
          );
        },
      };
    })),
    {
      title: "Total Qty.",
      key: "totalQty",
      width: 120,
      align: "center",
      onHeaderCell: () => ({ style: { background: "#094e8f", color: "#fff", fontWeight: 600 } }),
      render: (_, record) => {
        const totalReceived = Number(record?.invoiceQty) || 0;
        const distRows = qcUI[record.key] ?? [];
        const distributedSum = distRows.reduce(
          (s, r) => s + (Number(r.qty) || 0),
          0
        );
        return (
          <span>
            {distributedSum}/{totalReceived}
          </span>
        );
      },
    },
  ];

  const useDistributionPanelLayout =
    (isDistributionMode || isViewMode) && siteLocationList.length > 0;
  const distributionTableColumns = useDistributionPanelLayout
    ? distributionPanelColumns
    : columns2;

  const inspectionScrollX = 1220;
  const distributionScrollX = useDistributionPanelLayout
    ? 200 + 140 + 120 * siteLocationList.length + 120
    : 800;

  const [form] = Form.useForm();


const updateItemRow = (recordKey, field, value) => {
  setItems((prev) =>
    prev.map((r) => {
      if (r.key !== recordKey) return r;

      const isQtyField =
        field === "acceptedQty" || field === "holdQty" || field === "rejectedQty";
      if ((mode || "").toLowerCase() === "inspection" && isQtyField) {
        const total = Math.round(parseFloat(r.invoiceQty) || 0);
        const nextAccepted =
          field === "acceptedQty"
            ? Math.round(parseFloat(value) || 0)
            : Math.round(parseFloat(r.acceptedQty) || 0);
        const nextHold =
          field === "holdQty"
            ? Math.round(parseFloat(value) || 0)
            : Math.round(parseFloat(r.holdQty) || 0);
        const nextRejected =
          field === "rejectedQty"
            ? Math.round(parseFloat(value) || 0)
            : Math.round(parseFloat(r.rejectedQty) || 0);

        const sum = nextAccepted + nextHold + nextRejected;
        const isInvalid = total > 0 && sum > total;

        setQtyErrors((prevErr) => {
          const next = { ...prevErr };
          if (isInvalid) {
            next[recordKey] =
              `Accepted + Hold + Rejected quantity cannot exceed ${total}`;
          } else {
            delete next[recordKey];
          }
          return next;
        });

        const isOverTotal = total > 0 && sum > total;
        let acceptance = "Partially Accepted";
        if (isOverTotal) {
          acceptance = "Partially Accepted";
        } else if (total > 0 && nextAccepted === 0 && nextHold === 0 && nextRejected === total) {
          acceptance = "Rejected";
        } else if (total > 0 && sum === total) {
          acceptance = "Fully Accepted";
        } else {
          acceptance = "Partially Accepted";
        }

        return {
          ...r,
          acceptedQty: nextAccepted,
          holdQty: nextHold,
          rejectedQty: nextRejected,
          acceptance,
        };
      }

      return { ...r, [field]: value };
    })
  );
};




  const ACCEPTANCE_TO_API = {
    "Fully Accepted": "ACCEPT_ALL",
    Rejected: "REJECT_ALL",
    "Partially Accepted": "PARTIAL",
  };
  const REJECTION_REASON_TO_API = {
    "Damaged in Transit": "DAMAGED_IN_TRANSIT",
    "Quality Issue": "QUALITY_ISSUE",
    "Expired Material": "EXPIRED_MATERIAL",
    "Incorrect Specification": "INCORRECT_SPECIFICATION",
    "Quantity Mismatch": "QUANTITY_MISMATCH",
    "Lost in Transit": "LOST_IN_TRANSIT",
  };

  const handleSubmitInspection = async (saveAsDraft, opts = {}) => {
    if (mode !== "inspection") return;
    const currentDocId = receiptIds.documentId ?? id;
    const recordId = receiptIds.recordId;
    if (recordId == null && !currentDocId) {
      ShowErrorMsg("Document ID not found.");
      return;
    }
    if (!items?.length) {
      ShowErrorMsg("No items to submit.");
      return;
    }
    const missingReasonRow = items.find(
      (row) =>
        ((Number(row.holdQty) || 0) > 0 && !row.holdReason) ||
        ((Number(row.rejectedQty) || 0) > 0 && !row.rejectionReason)
    );
    if (missingReasonRow) {
      ShowErrorMsg(
        "Please select Hold Reason for items with hold quantity and Rejection Reason for items with rejected quantity before saving."
      );
      return;
    }

    const currentIsChallan = isChallanUi;
    const shouldSubmitForBoth = !!opts?.submitForBothDocTypes;

    const buildTargets = () => {
      const targets = [];
      if (!shouldSubmitForBoth) {
        targets.push({ docId: currentDocId, isChallan: currentIsChallan, isPrimary: true });
        return targets;
      }

      if (currentIsChallan) {
        // Challan UI: update challan + its linked invoice (if any)
        targets.push({ docId: currentDocId, isChallan: true, isPrimary: true });
        if (linkedDocIds.linkedInvoiceId) {
          targets.push({ docId: linkedDocIds.linkedInvoiceId, isChallan: false, isPrimary: false });
        }
      } else {
        // Invoice UI: update invoice + all linked challans (if any)
        targets.push({ docId: currentDocId, isChallan: false, isPrimary: true });
        (linkedDocIds.linkedChallanIds || []).forEach((cid) => {
          targets.push({ docId: cid, isChallan: true, isPrimary: false });
        });
      }

      // de-dup by (docId,isChallan)
      const seen = new Set();
      return targets.filter((t) => {
        const k = `${t.docId}__${t.isChallan}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    };

    const submitOnce = async (targetDocId, isChallanFlag, usePatch) => {
      const payload = {
        id: targetDocId ?? recordId,
        is_challan: isChallanFlag,
        grn_number:
          receiptHeader.grnNumber !== "—" ? receiptHeader.grnNumber : "",
        invoice_number:
          receiptHeader.invoiceRef !== "—" ? receiptHeader.invoiceRef : "",
        vendor: receiptIds.vendorId ?? undefined,
        site: receiptIds.siteId ?? undefined,
        save_as_draft: saveAsDraft,
        is_material_acceptance: true,
        user_details: inspectionUserDetails.map((u) => ({
          name: u.name || "",
          email: u.email || "",
          mobile: u.mobile || "",
        })),
        items: items.map((row) => ({
          id: row.id ?? row.key,
          item_name: row.item ?? "",
          total_received_qty: Number(row.invoiceQty) || 0,
          acceptance: ACCEPTANCE_TO_API[row.acceptance] ?? "PARTIAL",
          accepted_qty: Number(row.acceptedQty) || 0,
          hold_qty: Number(row.holdQty) || 0,
          rejected_qty: Number(row.rejectedQty) || 0,
          rejection_reason:
            (Number(row.rejectedQty) || 0) > 0 && row.rejectionReason
              ? row.rejectionReason
              : null,
          hold_reason:
            (Number(row.holdQty) || 0) > 0 && row.holdReason
              ? row.holdReason
              : null,
        })),
      };

      // Only use PATCH for the primary doc when we know it already has history.
      return usePatch && targetDocId
        ? patchRequest(
            `inventory-goods-receipts/${targetDocId}/update-inspection/`,
            payload
          )
        : postRequest(`inventory-goods-receipts/${targetDocId}/inspect/`, payload);
    };

    try {
      setSubmitting(true);
      const targets = buildTargets();
      const calls = targets.map((t) =>
        submitOnce(
          t.docId,
          t.isChallan,
          t.isPrimary && hasInspectionHistory,
        ),
      );

      const results = await Promise.all(calls);
      const res = results?.[0];
      if (embedded && onBack) onBack();
      else navigate("/inventory-goods-receipt-list");
      SuccessNotificationMsg(res?.data?.message ?? "Inspection report saved successfully");
    } catch (err) {
      ShowErrorMsg(err);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmSubmitInspectionReport = () => {
    Modal.confirm({
      title: "Send Inspection Report?",
      content:
        "This will save and send the inspection report. Do you want to continue?",
      okText: "Yes",
      cancelText: "No",
      onOk: () => handleSubmitInspection(false, { submitForBothDocTypes: true }),
    });
  };

  const handleSubmitDistribution = async (saveAsDraft, opts = {}) => {
    const isDistributionMode =
      (mode || "").toLowerCase() === "distribution";
    if (!isDistributionMode) return;
    const currentDocId = receiptIds.documentId ?? id;
    const recordId = receiptIds.recordId;
    if (recordId == null && !currentDocId) {
      ShowErrorMsg("Document ID not found.");
      return;
    }
    if (!items?.length) {
      ShowErrorMsg("No items to submit.");
      return;
    }

    const currentIsChallan = isChallanUi;
    const shouldSubmitForBoth = !!opts?.submitForBothDocTypes;

    const buildTargets = () => {
      const targets = [];
      if (!shouldSubmitForBoth) {
        targets.push({
          docId: currentDocId,
          isChallan: currentIsChallan,
          isPrimary: true,
        });
        return targets;
      }

      if (currentIsChallan) {
        // Challan UI: update challan + its linked invoice (if any)
        targets.push({ docId: currentDocId, isChallan: true, isPrimary: true });
        if (linkedDocIds.linkedInvoiceId) {
          targets.push({
            docId: linkedDocIds.linkedInvoiceId,
            isChallan: false,
            isPrimary: false,
          });
        }
      } else {
        // Invoice UI: update invoice + all linked challans (if any)
        targets.push({
          docId: currentDocId,
          isChallan: false,
          isPrimary: true,
        });
        (linkedDocIds.linkedChallanIds || []).forEach((cid) => {
          targets.push({ docId: cid, isChallan: true, isPrimary: false });
        });
      }

      const seen = new Set();
      return targets.filter((t) => {
        const k = `${t.docId}__${t.isChallan}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    };

    const submitOnce = async (targetDocId, isChallanFlag, usePatch) => {
      const payload = {
        id: recordId ?? targetDocId,
        is_challan: isChallanFlag,
        grn_number:
          receiptHeader.grnNumber !== "—" ? receiptHeader.grnNumber : "",
        invoice_number:
          receiptHeader.invoiceRef !== "—" ? receiptHeader.invoiceRef : "",
        vendor: receiptIds.vendorId ?? undefined,
        site: receiptIds.siteId ?? undefined,
        save_as_draft: saveAsDraft,
        is_material_acceptance: !saveAsDraft,
        user_details: distributionUserDetails.map((u) => ({
          name: u.name || "",
          email: u.email || "",
          mobile: u.mobile || "",
        })),
        items: items.map((row) => {
          const distRows = (qcUI[row.key] || []).filter(
            (r) => r.site != null && (Number(r.qty) || 0) > 0
          );
          const distribution_sites = distRows.map((r) => ({
            site_id: r.site,
            quantity: Number(r.qty) || 0,
            total_quantity: Number(row.invoiceQty) || 0,
          }));
          return {
            id: row.id ?? row.key,
            item_name: row.item ?? "",
            distribution_sites,
          };
        }),
      };

      // Only PATCH for the primary doc when we know it already has history.
      return usePatch && targetDocId
        ? patchRequest(
            `inventory-goods-receipts/${targetDocId}/update-inspection/`,
            payload
          )
        : postRequest(`inventory-goods-receipts/${targetDocId}/inspect/`, payload);
    };

    try {
      setSubmitting(true);
      const targets = buildTargets();
      const calls = targets.map((t) =>
        submitOnce(
          t.docId,
          t.isChallan,
          t.isPrimary && hasDistributionHistory,
        ),
      );
      const results = await Promise.all(calls);
      const res = results?.[0];

      if (embedded && onBack) onBack();
      SuccessNotificationMsg(
        res?.data?.message ?? "Distribution report saved successfully"
      );
    } catch (err) {
      ShowErrorMsg(err);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmSubmitDistributionReport = () => {
    Modal.confirm({
      title: "Send Distribution Report?",
      content:
        "This will save and send the distribution report. Do you want to continue?",
      okText: "Yes",
      cancelText: "No",
      onOk: () =>
        handleSubmitDistribution(false, { submitForBothDocTypes: true }),
    });
  };
const hasQtyErrors = Object.values(qtyErrors).some(Boolean);

  const isInspectionMode = (mode || "").toLowerCase() === "inspection";
  const inspectionQtyComplete =
    !isInspectionMode ||
    items.every((row) => {
      const total = Math.round(parseFloat(row.invoiceQty) || 0);
      const accepted = Math.round(parseFloat(row.acceptedQty) || 0);
      const hold = Math.round(parseFloat(row.holdQty) || 0);
      const rejected = Math.round(parseFloat(row.rejectedQty) || 0);
      const sum = accepted + hold + rejected;
      return total === sum;
    });
  const inspectionReasonsComplete =
    !isInspectionMode ||
    items.every((row) => {
      const hold = Number(row.holdQty) || 0;
      const rejected = Number(row.rejectedQty) || 0;
      return (hold === 0 || !!row.holdReason) && (rejected === 0 || !!row.rejectionReason);
    });
  const canSubmitInspectionReport =
    isInspectionMode && !hasQtyErrors && inspectionQtyComplete && inspectionReasonsComplete;

  return (
    <>
      {!embedded && (
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <Tooltip title="Goods received but pending QC">
              <h3>{t("Material Acceptance & Distribution")}</h3>
            </Tooltip>

            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to="/dashboard">Home</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/inventory-goods-receipt-list">
                  Inventory Goods Receipts
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Material Acceptance & Distribution</Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </div>
      </div>
      )}

      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body">
          {(selectedRadio === "radio1" || isViewMode) && (
            <div
              className="radio1cont"
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                ...(isViewMode && { marginBottom: 32 }),
              }}
            >
             <div className="po-header-details">
                <div className="po-row">
                  <strong>GRN Number :</strong>
                  <span>{receiptHeader.grnNumber}</span>
                </div>
                <div className="po-row">
                  <strong>{isChallanUi ? "Challan Ref." : "Invoice Ref."} :</strong>
                  <span>{receiptHeader.invoiceRef}</span>
                </div>
                <div className="po-row">
                  <strong>Supplier Name :</strong>
                  <span>{receiptHeader.supplierName}</span>
                </div>
              </div>
              {isViewMode && (
                <h4 className="pdf-card-title" style={{ marginTop: 16, marginBottom: 12 }}>Material Inspection</h4>
              )}
             
              <div className="tablewrp">
                <Table
                  columns={columns}
                  dataSource={items}
                  loading={loading}
                  rowKey="key"
                  scroll={{ x: inspectionScrollX }}
                  tableLayout={isViewMode ? "fixed" : undefined}
                  size="small"
                  pagination={false}
                />
              </div>

              {!isViewMode && (
                <div className="acceptance-dist-user-details">
                  <div className="acceptance-dist-user-details__header">
                  <h4 className="pdf-card-title" style={{ margin: 0 }}>
                          Notification Recipients
                          <Tooltip
                            color="#094E8F"
                            title="Add one or more recipients who will receive the notification via Email"
                          >
                            <InfoCircleOutlined
                              style={{ marginLeft: 6, color: "#094E8F", fontSize: 14 }}
                            />
                          </Tooltip>
                        </h4>
                    <Button
                      type="default"
                      icon={<PlusOutlined />}
                      onClick={addInspectionUserDetailRow}
                      size="small"
                    >
                      Add recipient
                    </Button>
                  </div>
                  {inspectionUserDetails.length > 0 ? (
                    <div className="PR-body" style={{ padding: 0 }}>
                      <div className="tablewrp">
                      <Table
                              dataSource={inspectionUserDetails}
                              rowKey="id"
                              size="middle"
                              pagination={false}
                              locale={{
                                emptyText: (
                                  <div style={{ padding: "24px 16px", color: "#8c8c8c", textAlign: "center" }}>
                                    No notification recipients added yet. Click &quot;Add recipient&quot; to add.
                                  </div>
                                ),
                              }}
                              scroll={{ x: 640 }}
                              columns={[
                                {
                                  title: "Name",
                                  dataIndex: "name",
                                  key: "name",
                                  width: 220,
                                  ellipsis: true,
                                  render: (val, row) => (
                                    <Input
                                      size="middle"
                                      value={val}
                                      placeholder="Name"
                                      onChange={(e) =>
                                        updateInspectionUserDetailRow(row.id, "name", e.target.value)
                                      }
                                    />
                                  ),
                                },
                                {
                                  title: "Email",
                                  dataIndex: "email",
                                  key: "email",
                                  width: 260,
                                  ellipsis: true,
                                  render: (val, row) => (
                                    <Input
                                      size="middle"
                                      value={val}
                                      placeholder="Email"
                                      onChange={(e) =>
                                        updateInspectionUserDetailRow(row.id, "email", e.target.value)
                                      }
                                    />
                                  ),
                                },
                                {
                                  title: "Mobile",
                                  dataIndex: "mobile",
                                  key: "mobile",
                                  width: 140,
                                  render: (val, row) => (
                                    <Input
                                      size="middle"
                                      value={val}
                                      placeholder="10 digits"
                                      maxLength={10}
                                      onChange={(e) => {
                                        const v = e.target.value
                                          .replace(/\D/g, "")
                                          .slice(0, 10);
                                        updateInspectionUserDetailRow(row.id, "mobile", v);
                                      }}
                                    />
                                  ),
                                },
                                {
                                  title: "",
                                  key: "action",
                                  width: 88,
                                  align: "center",
                                  render: (_, row) => (
                                    <Space size={4}>
                                      <Tooltip title="Add recipient">
                                        <Button
                                          type="text"
                                          size="small"
                                          icon={<PlusCircleOutlined />}
                                          onClick={addInspectionUserDetailRow}
                                          aria-label="Add recipient"
                                        />
                                      </Tooltip>
                                      <Tooltip title="Remove">
                                        <Button
                                          type="text"
                                          size="small"
                                          danger
                                          icon={<MinusCircleOutlined />}
                                          onClick={() => removeInspectionUserDetailRow(row.id)}
                                          aria-label="Remove recipient"
                                        />
                                      </Tooltip>
                                    </Space>
                                  ),
                                },
                              ]}
                            />
                      </div>
                    </div>
                  ) : (
                    <div className="acceptance-dist-user-details__empty">
                      No user details added. Click &quot;Add recipient&quot; to add.
                    </div>
                  )}
                </div>
              )}
              

              {!isViewMode && (
                <div className="btnwrap">
                  <Tooltip
                    title={
                      isInspectionMode && !inspectionReasonsComplete
                        ? "Please select Hold Reason and Rejection Reason for items with hold or rejected quantity."
                        : ""
                    }
                  >
                    <span>
                  <Button
                    type="primary"
                    className="SaveGradiantBtn"
                    loading={submitting}
                        disabled={hasQtyErrors || (isInspectionMode && !inspectionReasonsComplete)}
                    onClick={() => handleSubmitInspection(true)}
                  >
                    Save as Draft
                  </Button>
                    </span>
                  </Tooltip>

                  <Tooltip
                    title={
                      !canSubmitInspectionReport && isInspectionMode
                        ? !inspectionReasonsComplete
                          ? "Please select Hold Reason and Rejection Reason for items with hold or rejected quantity."
                          : "Accepted + Hold + Rejected quantity must equal total received quantity for each item."
                        : ""
                    }
                  >
                    <span>
                      <Button
                        type="primary"
                        className="SaveGradiantBtn"
                        loading={submitting}
                        disabled={!canSubmitInspectionReport}
                        onClick={confirmSubmitInspectionReport}
                      >
                        Save & Sent Inspection Report
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              )}
            </div>
          )}

          {(selectedRadio === "radio2" || isViewMode) && (
            <div
              className="radio2cont"
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                ...(isViewMode && { marginTop: 24 }),
              }}
            >
              {isViewMode && (
                <h4 className="pdf-card-title" style={{ marginTop: 0, marginBottom: 12 }}>Material Distribution</h4>
              )}
              {!isViewMode && (
              <div className="po-header-details">
               
                  <div className="po-row">
                    <strong>GRN Number :</strong>
                    <span>{receiptHeader.grnNumber}</span>
                  </div>
             
                <div className="po-row">
                  <strong>{isChallanUi ? "Challan Ref." : "Invoice Ref."} :</strong>
                  <span>{receiptHeader.invoiceRef}</span>
                </div>
                <div className="po-row">
                  <strong>Supplier Name :</strong>
                  <span>{receiptHeader.supplierName}</span>
                </div>
              </div>
              )}
              <div className="tablewrp">
                <Table
                  columns={distributionTableColumns}
                  dataSource={items}
                  loading={loading}
                  rowKey="key"
                  scroll={{ x: distributionScrollX }}
                  tableLayout={isViewMode ? "fixed" : undefined}
                  size="small"
                  pagination={false}
                />
              </div>

              {!isViewMode && (
                <div className="acceptance-dist-user-details">
                  <div className="acceptance-dist-user-details__header">
                    <h4 className="pdf-card-title" style={{ margin: 0 }}>
                      Notification Recipients
                      <Tooltip
                        color="#094E8F"
                        title="Add one or more recipients who will receive the notification via Email"
                      >
                        <InfoCircleOutlined
                          style={{ marginLeft: 6, color: "#094E8F", fontSize: 14 }}
                        />
                      </Tooltip>
                    </h4>
                    <Button
                      type="default"
                      icon={<PlusOutlined />}
                      onClick={addDistributionUserDetailRow}
                      size="small"
                    >
                      Add recipient
                    </Button>
                  </div>
                  {distributionUserDetails.length > 0 ? (
                    <div className="PR-body" style={{ padding: 0 }}>
                      <div className="tablewrp">
                        <Table
                          dataSource={distributionUserDetails}
                          rowKey="id"
                          size="middle"
                          pagination={false}
                          locale={{
                            emptyText: (
                              <div style={{ padding: "24px 16px", color: "#8c8c8c", textAlign: "center" }}>
                                No notification recipients added yet. Click &quot;Add recipient&quot; to add.
                              </div>
                            ),
                          }}
                          scroll={{ x: 640 }}
                          columns={[
                            {
                              title: "Name",
                              dataIndex: "name",
                              key: "name",
                              width: 220,
                              ellipsis: true,
                              render: (val, row) => (
                                <Input
                                  size="middle"
                                  value={val}
                                  placeholder="Name"
                                  onChange={(e) =>
                                    updateDistributionUserDetailRow(row.id, "name", e.target.value)
                                  }
                                />
                              ),
                            },
                            {
                              title: "Email",
                              dataIndex: "email",
                              key: "email",
                              width: 260,
                              ellipsis: true,
                              render: (val, row) => (
                                <Input
                                  size="middle"
                                  value={val}
                                  placeholder="Email"
                                  onChange={(e) =>
                                    updateDistributionUserDetailRow(row.id, "email", e.target.value)
                                  }
                                />
                              ),
                            },
                            {
                              title: "Mobile",
                              dataIndex: "mobile",
                              key: "mobile",
                              width: 140,
                              render: (val, row) => (
                                <Input
                                  size="middle"
                                  value={val}
                                  placeholder="10 digits"
                                  maxLength={10}
                                  onChange={(e) => {
                                    const v = e.target.value
                                      .replace(/\D/g, "")
                                      .slice(0, 10);
                                    updateDistributionUserDetailRow(row.id, "mobile", v);
                                  }}
                                />
                              ),
                            },
                            {
                              title: "",
                              key: "action",
                              width: 88,
                              align: "center",
                              render: (_, row) => (
                                <Space size={4}>
                                  <Tooltip title="Add recipient">
                                    <Button
                                      type="text"
                                      size="small"
                                      icon={<PlusCircleOutlined />}
                                      onClick={addDistributionUserDetailRow}
                                      aria-label="Add recipient"
                                    />
                                  </Tooltip>
                                  <Tooltip title="Remove">
                                    <Button
                                      type="text"
                                      size="small"
                                      danger
                                      icon={<MinusCircleOutlined />}
                                      onClick={() => removeDistributionUserDetailRow(row.id)}
                                      aria-label="Remove recipient"
                                    />
                                  </Tooltip>
                                </Space>
                              ),
                            },
                          ]}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="acceptance-dist-user-details__empty">
                      No notification recipients added yet. Click &quot;Add recipient&quot; to add.
                    </div>
                  )}
                </div>
              )}

              {!isViewMode && (
                <div className="btnwrap">
                  <Button
                    type="primary"
                    className="SaveGradiantBtn"
                    loading={submitting}
                    disabled={hasQtyErrors}
                    onClick={() => handleSubmitDistribution(true)}
                  >
                    Save as Draft
                  </Button>

                  <Button
                    type="primary"
                    className="SaveGradiantBtn"
                    loading={submitting}
                    disabled={hasQtyErrors}
                    onClick={confirmSubmitDistributionReport}
                  >
                    Save & Sent Distribution Report
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

AcceptanceDist.propTypes = {
  embedded: PropTypes.bool,
  modeProp: PropTypes.string,
  idProp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBack: PropTypes.func,
  documentType: PropTypes.string,
};

export default AcceptanceDist;

